import { Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DaisyAvatarComponent, DaisyTooltipDirective } from '@pathfinder/daisy-controls/ui';
import { RouterModule } from '@angular/router';
import { NotificationService } from '@lib/notification/data-access';
import { NotificationCategory, SystemNotificationModel } from '@lib/notification/models';
import { TimeAgoPipe } from '@lib/shared/util';
import { NotificationModel } from '@lib/notification/models';
import { ToastMessageService } from '@lib/shared/data-access';
import { AuthService } from '@lib/auth/data-access';

@Component({
  selector: 'lib-feature-system-notification-template',
  imports: [
    CommonModule,
    RouterModule,
    TimeAgoPipe,
    DaisyAvatarComponent,
    DaisyTooltipDirective,
  ],
  templateUrl: './system-notification-template.component.html',
  styleUrl: './system-notification-template.component.scss',
})
export class SystemNotificationTemplateComponent {  
  public notificationService = inject(NotificationService);
  public authService = inject(AuthService);
  private toastMessageService = inject(ToastMessageService);

  /**
   * The panel's Only show unread toggle. It narrows the system notifications, which carry a read state; the session's
   * errors are listed regardless - they are dismissed rather than read, and opening System already marks them read.
   */
  public unreadOnly = input<boolean>(false);

  public systemErrorNotificationsComputed = this.notificationService.systemNotificationsComputedSignal;
  public systemNotificationsComputed = computed(() => {
    const unreadOnly = this.unreadOnly();
    return this.notificationService
      .notificationsComputedSignal()
      .filter(x => x.category === NotificationCategory.System && (!unreadOnly || !x.isRead));
  });

  onRemoveSystemNotificationClick(notification: SystemNotificationModel) {
    this.notificationService.removeSystemNotification(notification);
  }

  onCopyOperationIdClick(notification: SystemNotificationModel) {
    navigator.clipboard.writeText(notification.operationId);

    notification.isOperationIdCopied = true;
    setTimeout(() => {
      notification.isOperationIdCopied = false;
    }, 2000);
  }

  public onHyperLinkClick(notification: NotificationModel, $event: MouseEvent) {
    if ($event) $event.stopPropagation();

    this.onMarkNotificationsReadStatusesClick(notification, true, $event);
  }

  public onMarkNotificationsReadStatusesClick(notifications: NotificationModel[] | NotificationModel, markAsRead: boolean, event?: MouseEvent) {
    if (event) event.stopPropagation();

    // Ensure notifications is always treated as an array
    const notificationsArray = Array.isArray(notifications) ? notifications : [notifications];

    // Filter out any notifications without an id and update their read status
    const updatedNotifications = notificationsArray.filter(notification => notification.id !== undefined);

    updatedNotifications.forEach(notification => {
      notification.isRead = markAsRead;
    });

    if (updatedNotifications.length > 0) {
      this.notificationService.updateNotificationsStatus(updatedNotifications).subscribe({
        error: () => this.toastMessageService.showError("Error updating notification status.")
      });
    }
  }
}
